import config from "./config";
import WildEmitter from "../lib/wild-emitter";
import {logError, logInfo} from "./log";
import {IPeerUserInfo, ISPeer, ITextChatMessage} from "./appState";
import io = require("socket.io-client");

type SendType =
    "get servers"
    | "auth"
    | "chat"
    | "attention"
    | "disconnectPatients"
    | "signal"
    | "join"
    | "fileUploadProcess"
    | "fileUploadStart"
    | "update online status"
    | "getUserInfo"
    | "getChatMessages"
    | "registerForConsultation";

class VChatComm extends WildEmitter {
    private readonly dataSet: { [key: string]: any } = {};
    private readonly connection: SocketIOClient.Socket;
    private authorized: boolean = false;
    private connected: boolean = false;
    private connectionError: any;

    constructor() {
        super();

        this.connection = io.connect(config.ssUrl, {autoConnect: false});

        this.connection.on("disconnect", () => {
            this.authorized = false;
            this.emit("deauthorized");
        });

        this.connection.on("connect", async () => {
            this.connected = true;

            // Авторизация
            const user = this.getData<IUser>("user");
            if (!user) {
                logError("No user");
                return;
            }

            try {
                await this.send("auth", user);
            } catch (e) {
                logError(e);
                return;
            }

            this.authorized = true;
            this.emit("authorized");

            // Прошла авторизация пользователя на сервере
            logInfo("Авторизирован");
        });

        this.connection.on("connect_error", (e: any) => {
            const request = e.context;
            this.connectionError = request ?? true;
            this.connection.close();
        });
    }

    connect() {
        this.connection.connect();
    }

    get sessionId() {
        return this.connection.id;
    }

    addData(key: string, data: any) {
        this.dataSet[key] = data;
    }

    getData<T>(key: string): T | undefined {
        return this.dataSet[key];
    }

    send(msg: "get servers"): Promise<(RTCIceServer)[]>;
    send(msg: "auth", user: IUser): Promise<void>;
    send(msg: "chat", text: string): Promise<void>;
    send(msg: "attention"): Promise<void>;
    send(msg: "disconnectPatients"): Promise<void>;
    send<T extends string, P extends any>(msg: "signal", message: OutgoingMessage<T, P>): Promise<void>;
    send(msg: "join", name: string): Promise<RoomDescription>;
    send(msg: "fileUploadProcess", data: { name: File["name"], data: FileReader["result"] }): Promise<void>;
    send(msg: "fileUploadStart", data: { name: File["name"], size: File["size"] }): Promise<void>;
    send(msg: "update online status", consIds: number[]): Promise<void>;
    send(msg: "getUserInfo", peerId: ISPeer["id"]): Promise<IPeerUserInfo>;
    send(msg: "getChatMessages"): Promise<ITextChatMessage[]>;
    send(msg: "registerForConsultation", jwt: string): Promise<ConsResponse>;
    send<T>(msg: SendType, ...args: any[]): Promise<T> {
        return new Promise<T>((resolve, reject) =>
            this.connection.emit(msg, ...args, (error: string, data: T) => {
                if (error) {
                    logError("Server error:", error);
                    reject(error);
                    return;
                }

                resolve(data);
            }));
    }

    onAuthorized(cb: Function) {
        if (this.authorized) {
            cb.call(this);
        }

        this.on("authorized", () => cb.call(this));
    }

    onConnected(cb: Function) {
        if (this.connected) {
            cb.call(this);
            return;
        }

        this.connection.on("connect", () => cb.call(this));
    }

    onConnectError(cb: Function) {
        if (this.connectionError) {
            cb.call(this);
            return;
        }

        this.connection.on("connect_error", () => cb.call(this));
    }

    waitForConnection() {
        return new Promise<void>(resolve => {
            if (this.connected) {
                resolve();
            } else {
                this.connection.once("connect", () => resolve());
            }
        });
    }

    onReceive(event: string, cb: Function) {
        return this.connection.on(event, (...args: any[]) => {
            // console.info("Comm received", event, ...args);
            cb(...args);
        });
    }
}

const vChatComm = new VChatComm();

export default vChatComm;
